import { TemplateRef } from '@angular/core';
import { Column, ColumnConfig } from './column';
import { ColumnType } from './column-type.enum';

type TemplateColumnConfig = ColumnConfig;

interface TemplateColumnContext<T> {
  row: T;
  toString: () => string;
}

export class TemplateColumn<T> extends Column<T> {
  public readonly columnType = ColumnType.Template;
  public config: TemplateColumnConfig = {
    cellClasses: []
  };

  /**
   * A column that displays a template.
   *
   * The template column takes a reference to any TemplateRef.
   * The template will be given a template variable named `row` containing the model for the row.
   *
   * This is a bit complicated, so see the example.
   *
   * @example
   * ///// component.html
   * <app-table [columns]="tableColumns" [data]="tableData">
   *   <ng-template #sprocketColumn let-sprocket="row">
   *     <!--
   *       Because we wrote `let-sprocket="row"`, `sprocket` is now available as a template variable.
   *      `sprocket` is equal to the Sprocket object for this row. Now we can display a highly customised cell template with it.
   *     -->
   *     <button *ngIf="!sprocket.isFobbed">Fob this sprocket</button>
   *     <span *ngIf="sprocket.isFobbed">Sprocket is fobbed</span>
   *   </ng-template>
   * </app-table>
   *
   * ///// component.ts
   * // Preferably use { static: true }. It makes a difference for Angular's change detection.
   * // Specifically, { static: true } means the template can be loaded *immediately* for use by the table.
   * // (Also, please ignore the initial dot, it's just to avoid breaking jsdoc.)
   * .@ViewChild('sprocketColumn', { static: true }) private readonly sprocketColumn: TemplateRef<any>;
   * public data: Sprocket[];
   * public readonly tableColumns = [
   *   new TemplateColumn<Sprocket>('Fob status', () => this.sprocketColumn),
   * ];
   *
   * @see {@link https://angular.io/api/common/NgTemplateOutlet|Angular: NgTemplateOutlet}
   *   The angular documentation on NgTemplateOutlet & NgTemplateOutletContext
   * @see {@link https://blog.angular-university.io/angular-ng-template-ng-container-ngtemplateoutlet/|Angular University: NgTemplateOutlet}
   *   The in-depth Angular University explanation for the template outlet and its context object.
   *
   * @param title The column title
   * @param getTemplateRef This function must return a TemplateRef to use as the contents of each cell.
   * @param extraConfig Any additional config for this column.
   */
  constructor(
    public readonly title: string,
    public readonly getTemplateRef: () => TemplateRef<any>,
    public extraConfig?: TemplateColumnConfig,
  ) {
    super();
    this.applyConfig(extraConfig);
  }

  public toString(): string {
    return `[TemplateColumn (title: ${this.title})]`;
  }

  /**
   * Get the template context object for the row.
   *
   * @see {@link https://angular.io/api/common/NgTemplateOutlet|Angular: NgTemplateOutlet}
   *   The angular documentation on NgTemplateOutletContext
   * @see {@link https://blog.angular-university.io/angular-ng-template-ng-container-ngtemplateoutlet/|Angular University: NgTemplateOutlet}
   *   The in-depth Angular University explanation for the template outlet and its context object.
   *
   * @param row The row being displayed in this table.
   * @returns The template context. This consists of the variable `row` defined to the current row's model.
   */
  public getContext(row: T): TemplateColumnContext<T> {
    return {
      row,
      toString: () => `[TemplateColumnContext (title: ${this.title}, contains: ${(row as Object).constructor?.name})]`,
    };
  }
}
